/**
 * Store submission settings (P29, SPEC §39.4, api/openapi.yaml Store Apps): store apps, the per-environment track rules, and
 * the submit settings of a build profile per environment (the console's equivalent of an EAS submit profile).
 */
import { z } from 'zod';
import { Platform, uuid } from './common.js';

/** A Play track id: internal, alpha (closed testing), beta (open testing), production, or a custom closed track (pre-prod). */
export const PLAY_TRACK_PATTERN = /^[a-z0-9][a-z0-9:_-]*$/;
/** An Android package name: letters, digits and `_`, at least two dot-separated parts, each starting with a letter. */
export const ANDROID_APPLICATION_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;

const track = z.string().min(1).max(64).regex(PLAY_TRACK_PATTERN, 'a Play track id: lowercase letters, digits, ":", "_" and "-"');
const fraction = z.number().gt(0).lt(1);

/**
 * How builds of a profile in one environment are submitted. `releaseStatus: inProgress` is a staged rollout and needs
 * `rolloutFraction`; the other statuses take none. Whether the track is allowed is checked when a submission is made.
 */
export const SubmitSettings = z.object({
  autoSubmit: z.boolean().default(false),
  track,
  releaseStatus: z.enum(['completed', 'draft', 'inProgress']).default('completed'),
  rolloutFraction: fraction.nullable().optional(),
  changesNotSentForReview: z.boolean().default(false),
}).strict().superRefine((s, ctx) => {
  if (s.releaseStatus === 'inProgress' && !s.rolloutFraction) ctx.addIssue({ code: 'custom', path: ['rolloutFraction'], message: 'a staged rollout (releaseStatus inProgress) needs rolloutFraction' });
  if (s.releaseStatus !== 'inProgress' && s.rolloutFraction != null) ctx.addIssue({ code: 'custom', path: ['rolloutFraction'], message: 'rolloutFraction is only for a staged rollout (releaseStatus inProgress)' });
});
export type SubmitSettings = z.infer<typeof SubmitSettings>;

const UploadCert = z.object({ sha1: z.string().trim().min(1).max(128).optional(), sha256: z.string().trim().min(1).max(128).optional() }).strict();

export const StoreAppCreate = z.object({
  appId: uuid,
  platform: Platform,
  identifier: z.string().trim().min(1).max(255),
  credentialId: uuid,
  externalAppId: z.string().trim().min(1).max(64).nullable().optional(),
  isPrivateManaged: z.boolean().default(false),
  managedPublishing: z.boolean().default(false),
  expectedUploadCert: UploadCert.nullable().optional(),
}).strict();
export type StoreAppCreate = z.infer<typeof StoreAppCreate>;

export const StoreAppPatch = z.object({
  credentialId: uuid.optional(),
  externalAppId: z.string().trim().min(1).max(64).nullable().optional(),
  isPrivateManaged: z.boolean().optional(),
  managedPublishing: z.boolean().optional(),
  expectedUploadCert: UploadCert.nullable().optional(),
}).strict().refine((p) => Object.keys(p).length > 0, 'nothing to change');
export type StoreAppPatch = z.infer<typeof StoreAppPatch>;

export const DistributionRuleWrite = z.object({
  storeAppId: uuid,
  environmentId: uuid,
  allowedTracks: z.array(track).min(1).max(20),
  maxInitialRollout: z.number().gt(0).max(1).nullable().optional(),
}).strict();
export type DistributionRuleWrite = z.infer<typeof DistributionRuleWrite>;

/** `PUT /apps/{appId}/distribution-rules`: the full matrix; the read-only `appId` and `etag` of a `GET` may be sent back. */
export const DistributionRulesWrite = z.object({
  appId: uuid.optional(),
  etag: z.string().optional(),
  rules: z.array(DistributionRuleWrite.extend({ id: uuid.optional(), environment: z.string().optional() })).max(200),
}).strict();
export type DistributionRulesWrite = z.infer<typeof DistributionRulesWrite>;

/**
 * `POST /submissions` (P29 phase 2): one store upload of a build's store artifact. What the request leaves out comes from
 * the build profile's settings for the build's environment (store app, track, release status, rollout). A production
 * track needs `confirmProduction: true`. Exactly one of `buildId` or `artifactId`. Hangar uploads through the build service that
 * made the build (`eas submit`, Bitrise's deploy step), so the request names none.
 */
export const SubmissionCreateRequest = z.object({
  buildId: uuid.optional(),
  artifactId: uuid.optional(),
  storeAppId: uuid.optional(),
  track: track.optional(),
  releaseStatus: z.enum(['completed', 'draft', 'inProgress']).optional(),
  rolloutFraction: fraction.optional(),
  changesNotSentForReview: z.boolean().optional(),
  releaseNotes: z.record(z.string().min(2).max(16), z.string().max(500)).optional(),
  confirmProduction: z.boolean().optional(),
}).strict().refine((r) => (r.buildId ? 1 : 0) + (r.artifactId ? 1 : 0) === 1, { path: ['buildId'], message: 'send exactly one of buildId or artifactId' });
export type SubmissionCreateRequest = z.infer<typeof SubmissionCreateRequest>;

export const SubmissionListQuery = z.object({
  buildId: uuid.optional(),
  storeAppId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
  /** `events` embeds each submission's timeline. */
  include: z.string().optional(),
}).strict();
export type SubmissionListQuery = z.infer<typeof SubmissionListQuery>;
